import _ from "lodash"
import { useContext, useState, useEffect, useMemo, useRef, useCallback } from "react"
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom"
import { setContext } from "@apollo/client/link/context"
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  useQuery,
  useLazyQuery,
} from "@apollo/client"

// Server URL
import serverUrl from "../config/serverUrl"

// Queries
import * as QUERIES from "./queries"

// Context
import { UserContext } from "../context/userContext"
import { FirebaseContext } from "../firebase"
import { UserActivityContext } from "../context/userActivityContext"

// Comps
import RoutesBuilder from "../components/Routes"

// Loaders
import LoaderFullPage, {
  LoaderProgressBarSideWide,
} from "../components/Loaders"

// Pages
import Login from "../pages/Login"
import Authentication from "../pages/Authentication"
import ROUTES from "../components/Routes/routesContants"

import {
  getSessStorage,
  setSessStorage,
  clearSessStorage,
} from "../helpers/sessionStorage"
import { LogError } from "../helpers/logger"
import { AuthProvider, useAuth } from "../context/authContext"

// Cache Component
import WithClearCache from "../components/WithClearCache"

//Helper
import updateAppUser from "../helpers/appUser"

// Cloud Functions
import { useActivityTracker } from "../hooks/activityTracker/useActivityTracker"
import UserActivityWarning from "../components/UserActivityWarning"
import useTokenManager from "../hooks/tokenManager/useTokenManager"
import { callCloudFunction } from "../helpers/cloudFunctionRequests"

const AUTH_ROUTE_SIGNOUT_ERROR_KEY = "auth_route_signout_error"
const INITIAL_TOKEN_RECOVERY_SIGNOUT_TIMEOUT_MS = 10000

function HandleIsAuth(props) {
  const { session, authUser, handleMainTokenRefresh, signOut } = props
  const navigate = useNavigate()
  // Prevent repeated signOut redirects while this component re-renders.
  const handledInitialTokenFailureRef = useRef(false)
  const [initialTokenRecoveryBusy, setInitialTokenRecoveryBusy] = useState(false)
  const [initialTokenRecoveryError, setInitialTokenRecoveryError] =
    useState("")
  const [initialTokenRecoveryRetryTick, setInitialTokenRecoveryRetryTick] =
    useState(0)

  const [globalLoading, setGlobalLoading] = useState(false)

  //Call ctx
  const [audioToken, setAudioToken] = useState(null)
  const [callStatus, setCallStatus] = useState("No call in progress.")
  const [isMuted, setIsMuted] = useState(false)
  const [isOnCall, setIsOnCall] = useState(false)
  const [startCallEvent, setStartCallEvent] = useState(false)
  const [hangupEvent, setHangupEvent] = useState(false)
  const [muteOnOffEvent, setMuteOnOffEvent] = useState(false)
  const [isDeviceReady, setIsDeviceReady] = useState(false)
  const [callError, setCallError] = useState("")
  const [showCarrierBlockCallError, setShowCarrierBlockCallError] =
    useState(false)

  const defaultFromSession = {
    current_organization_id: session.user.organization_id,
    current_participant: session.participant,
    current_participant_id:
      getSessStorage("participant_id") !== ""
        ? getSessStorage("participant_id")
        : null,
    current_staff_membership_id:
      getSessStorage("current_staff_membership_id") !== ""
        ? getSessStorage("current_staff_membership_id")
        : null,
  }

  const [current_participant_id, setCurrentPid] = useState(() =>
    getSessStorage("participant_id") || null,
  )
  const [current_organization_id, setCurrentOrganizationId] = useState(
    () => session.user.organization_id,
  )
  const [participantMembership, setParticipantMembership] = useState(
    () => session.participant,
  )

  const [current_staff_membership_id, setCurrentStaffMembershipId] = useState(
    () => getSessStorage("current_staff_membership_id") || null,
  )

  const [current_staff, setCurrentStaff] = useState(session.user) // TODO: Check defaults
  const [currentActiveReferral, setCurrentActiveReferral] = useState(null)

  //Notifications settings
  const [showNotificationsSettings, setShowNotificationsSettings] =
    useState(false)

  //Housing settings
  const [housing, setHousing] = useState({ showHousing: false })

  const [refreshTrigger, setRefreshTrigger] = useState(false)
  const [toggleAppointment, setToggleAppointment] = useState({
    toggle: false,
    page: "default",
  })

  //Manages token lifetimes and refreshing
  const {
    initialTokenFetchDone,
    initialTokenFetchError,
    hasMainToken,
    getMainToken,
    getNotificationToken,
    getTaskToken,
    getDocumentSignToken,
    getAppointmentsToken,
    getHousingToken,
    getTelerecoveryToken,
    getEmploymentToken,
    getAssessmentsToken,
    getGpraToken,
    getCoordinationBoardsToken,
    getKioskToken,
    getGooglePlacesKey,
    getMapBoxKey,
  } = useTokenManager(authUser, handleMainTokenRefresh)

  //Tracks user activity and handles automatic logout after inactivity.
  const {
    state,
    countdown,
    getLogoutAfter,
    getWarningDuration,
    getLastActive,
    resetTimer,
    logout,
    refreshSettings,
  } = useActivityTracker({
    doMonitor: !!session?.user?.staff_id, // Only monitor activity if there is a logged in user
    mainTokenReady: hasMainToken,
    onWarning: () => {},
  })

  useEffect(() => {
    const userRole = session?.user?.staff_role
    const organizationType = session?.user?.organization_type
    if (userRole >= 9 && organizationType === "PARTNER") {
      navigate("/referrals/list")
    }
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.organization_type, session?.user?.staff_role])

  async function setToggleAppointments(val) {
    setToggleAppointment(val)
  }

  async function refreshData() {
    setRefreshTrigger(true)
    setRefreshTrigger(false)
  }

  // IncomingCall
  const [incomingCall_QueueId, setIncomingCallQueueId] = useState(null)
  const [incomingCall_EventId, setIncomingCallEventId] = useState(null)
  const [incomingCall_PickedUp, setIncomingCallPickedUp] = useState(null)

  // Global Messages
  const [gmParticipantsConvList, setGmParticipantsConvList] = useState([])

  // System notifications
  const [notification_refresh, setNotificationRefresh] = useState(undefined)
  const [notification_Information, setNotificationInformation] =
    useState(undefined)
  const [notification_error, setNotificationError] = useState(undefined)
  const [notification_action, setNotificationAction] = useState(undefined)
  const [notification_show_state, setNotificationShowState] = useState("")

  // System tasks
  const [notification_closed, setNotificationsClosed] = useState(undefined)

  // Document Signing
  const [triggerDocumentRequestShow, setTriggerDocumentRequestShow] =
    useState(undefined)
  const [documentsQuickFilter, setDocumentsQuickFilter] = useState(undefined)

  function showDocumentRequests(quickFilterOption) {
    setDocumentsQuickFilter(quickFilterOption)
    setTriggerDocumentRequestShow(!triggerDocumentRequestShow)
  }

  const [canFetch, setCanFetch] = useState(false)

  const retryInitialTokenRecovery = useCallback(() => {
    setInitialTokenRecoveryError("")
    handledInitialTokenFailureRef.current = false
    setInitialTokenRecoveryRetryTick((tick) => tick + 1)
  }, [])

  useEffect(() => {
    const pendingSignOutError = getSessStorage(AUTH_ROUTE_SIGNOUT_ERROR_KEY)
    if (!pendingSignOutError) return

    setNotificationError(pendingSignOutError)
    clearSessStorage(AUTH_ROUTE_SIGNOUT_ERROR_KEY)
  }, [])

  useEffect(() => {
    updateAppUser({
      organization_id: current_organization_id ?? null,
      participant_id: current_participant_id ?? null,
      enterprise_id: current_staff?.enterprise_id ?? null,
    })
  }, [
    current_staff?.enterprise_id,
    current_organization_id,
    current_participant_id,
  ])

  useEffect(() => {
    if (
      !_.isEmpty(current_participant_id) &&
      !_.isEmpty(current_organization_id)
    ) {
      setCanFetch(true)
    } else {
      console.warn(
        "Invalid participant_id or organization_id:",
        defaultFromSession
      )
      setCanFetch(false)
    }
  }, [
    current_organization_id,
    current_participant_id,
  ])

  // Trigger if Participant is in SessionStorage
  const { loading: fetchingParticipant } = useQuery(QUERIES.GET_PARTICIPANT, {
    skip:
      !hasMainToken ||
      !canFetch ||
      !current_participant_id ||
      current_participant_id === "" ||
      !current_organization_id ||
      current_organization_id === "",
    variables: {
      participant_id: current_participant_id,
      organization_id: current_organization_id,
    },
    onCompleted: ({ participant }) => {
      setParticipantMembership(participant)
    },
    onError: (error) => {
      console.error(
        "useQuery: Error when fetching Participant from Session Storage.",
        error,
      )
    },
  })

  // TODO: No need fetching User with new Session...
  const [getCurrentStaff] = useLazyQuery(QUERIES.GET_USER, {
    onCompleted: ({ user }) => {
      setCurrentStaff(user)
    },
  })

  const [getCurrentParticipant] = useLazyQuery(QUERIES.GET_PARTICIPANT, {
    fetchPolicy: "no-cache",
    onCompleted: ({ participant }) => setParticipantMembership(participant),
    onError: ({ error }) =>
      console.log("useLazyQuery: Error when getting Participant:", error),
  })

  const [getParticipantByPhoneNumber] = useLazyQuery(
    QUERIES.GET_PARTICIPANT_BY_PHONE,
    {
      fetchPolicy: "network-only",
    },
  )

  const [getCurrentParticipantByAliasID] = useLazyQuery(
    QUERIES.GET_PARTICIPANT_BY_ALIAS_ID,
    {
      fetchPolicy: "network-only",
    },
  )

  async function setParticipantByPhone(participantPhoneNumber) {
    await getParticipantByPhoneNumber({
      variables: {
        phone: participantPhoneNumber,
      },
      fetchPolicy: "network-only",
      onCompleted: async ({ participantByNumber }) => {
        if (participantByNumber) {
          await changeParticipant(participantByNumber.participant_id)
        }
      },
      onError: ({ error }) => {
        console.log(error)
      },
    })
  }

  async function setParticipantByAliasID(participant_alias) {
    await getCurrentParticipantByAliasID({
      variables: {
        participant_alias: participant_alias,
        staff_id: current_staff.staff_id,
        organization_id: current_staff.organization_id,
      },
      fetchPolicy: "network-only",
      onCompleted: async ({ participantAdvancedSearch }) => {
        const participant_id =
          participantAdvancedSearch[0].participant_id || null
        if (participant_id) {
          await changeParticipant(participant_id)
        }
      },
      onError: (error) => {
        console.log(error)
      },
    })
  }

  // TODO: check if refactor is possible, look site-wide
  async function setStaff(staff_id, organization_id) {
    getCurrentStaff({
      variables: { staff_id, organization_id },
    })
    setCurrentOrganizationId(organization_id)
    clearSessStorage("current_organization_id")
    setSessStorage("current_organization_id", organization_id)
    return staff_id
  }

  // TODO: Legacy, should use changeParticipant instead
  const setParticipant = async (participant_id) => {
    const resetParticipant =
      participant_id === null || participant_id === undefined
    if (resetParticipant) {
      // null clears the current participant
      return await changeParticipant(null)
    }
    await changeParticipant(participant_id)
  }

  const clearParticipant = async () => await changeParticipant(null)

  async function changeParticipant(participant_id) {
    if (participant_id === null) {
      setCurrentPid(null)
      clearSessStorage("participant_id")
      return setParticipantMembership({})
    }
    setGlobalLoading(true)
    setCurrentPid(participant_id)
    getCurrentParticipant({
      variables: {
        participant_id,
        organization_id: session.user.organization_id,
      },
      onCompleted: ({ participant }) => {
        setSessStorage("participant_id", participant_id)
        setParticipantMembership(participant)
        return setGlobalLoading(false)
      },
      onError: ({ error }) => {
        console.log("Error while changing participant", error)
        clearSessStorage("participant_id")
        return setGlobalLoading(false)
      },
    })
    setTimeout(() => {
      setGlobalLoading(false)
    }, 1000)
  }

  async function changeEnterprise(enterprise_id) {
    setGlobalLoading(true)
    changeParticipant(null)
    const params = {
      token: getMainToken(),
      enterprise_id,
      sessionId: session.user.staff_id,
    }
    try {
      const responseEnterprise= await callCloudFunction("setSessionCurrentEnterprise", params, getMainToken())


      const organization_id = responseEnterprise.data.data.organization_id
      setCurrentOrganizationId(organization_id)
      return setGlobalLoading(false)
    } catch (error) {
      console.log("Error while changing enterprise", error)
      return setGlobalLoading(false)
    }
  }

  async function changeOrganization(organization_id) {
    setGlobalLoading(true)
    changeParticipant(null)

    const params = {
      token: getMainToken(),
      organization_id,
      sessionId: session.user.staff_id,
    }
    
    try {
      await callCloudFunction("setSessionCurrentOrg", params, getMainToken())

      setCurrentOrganizationId(organization_id)
      return setGlobalLoading(false)
    } catch (error) {
      console.log("Error while changing organization", error)
      return setGlobalLoading(false)
    }
  }

  function changeCurrentStaffMembershipId(staff_membership_id) {
    if (staff_membership_id === null) {
      setCurrentStaffMembershipId(null)
      clearSessStorage("current_staff_membership_id")
      return setParticipantMembership({})
    }
    setSessStorage("current_staff_membership_id", staff_membership_id)
    setCurrentStaffMembershipId(staff_membership_id)
  }

  // Check on current_participant_id before spreading participantMembership.
  const participantData = current_participant_id ? participantMembership : {}

  const ctxValue = {
    ...session.user, // TODO: Should be rm and use sessions instead
    organization_name: _.unescape(session.user.organization_name),
    participant_id: current_participant_id || "",
    ...participantData,
    globalLoading,
    changeEnterprise,
    changeOrganization,
    changeParticipant,
    changeCurrentStaffMembershipId,
    clearParticipant,
    current_organization_id,
    organization_id: current_organization_id || "",
    current_participant_id,
    current_staff_membership_id,
    documentsQuickFilter,
    gmParticipantsConvList,
    incomingCall_EventId,
    incomingCall_PickedUp,
    incomingCall_QueueId,
    notification_Information,
    notification_action,
    notification_error,
    notification_refresh,
    notification_closed,
    notification_show_state,
    refreshData,
    setGlobalLoading,
    session,
    refreshTrigger,
    setGmParticipantsConvList,
    setIncomingCallEventId,
    setIncomingCallPickedUp,
    setIncomingCallQueueId,
    setNotificationAction,
    setNotificationError,
    setNotificationInformation,
    setNotificationRefresh,
    setNotificationsClosed,
    setNotificationShowState,
    setParticipant,
    setParticipantByAliasID,
    setParticipantByPhone,
    setStaff,
    setToggleAppointments,
    showDocumentRequests,
    toggleAppointment,
    triggerDocumentRequestShow,
    currentActiveReferral,
    setCurrentActiveReferral,
    showNotificationsSettings,
    setShowNotificationsSettings,
    tokens: {
      getMainToken,
      getAppointmentsToken,
      getDocumentSignToken,
      getHousingToken,
      getNotificationToken,
      getTaskToken,
      getTelerecoveryToken,
      getAssessmentsToken,
      getEmploymentToken,
      getGpraToken,
      getKioskToken,
      getCoordinationBoardsToken,
    },
    keys: {
      getGooglePlacesKey,
      getMapBoxKey,
    },
    housing: {
      context: housing,
      setHousing,
    },
    call: {
      audioToken,
      setAudioToken,
      callStatus,
      setCallStatus,
      isOnCall,
      setIsOnCall,
      isMuted,
      setIsMuted,
      startCallEvent,
      setStartCallEvent,
      hangupEvent,
      setHangupEvent,
      muteOnOffEvent,
      setMuteOnOffEvent,
      isDeviceReady,
      setIsDeviceReady,
      callError,
      setCallError,
      showCarrierBlockCallError,
      setShowCarrierBlockCallError,
    },
  }

  const userActivityContextValue = useMemo(
    () => ({
      getLogoutAfter,
      getWarningDuration,
      state,
      countdown,
      getLastActive,
      resetTimer,
      logout,
      refreshSettings,
    }),
    [
      getLogoutAfter,
      getWarningDuration,
      state,
      countdown,
      getLastActive,
      resetTimer,
      logout,
      refreshSettings,
    ],
  )

  useEffect(() => {
    if (!initialTokenFetchError || hasMainToken) {
      handledInitialTokenFailureRef.current = false
      setInitialTokenRecoveryBusy(false)
      setInitialTokenRecoveryError("")
      return
    }

    if (handledInitialTokenFailureRef.current) return
    handledInitialTokenFailureRef.current = true

    let cancelled = false

    const recoverWithSignOut = async () => {
      setInitialTokenRecoveryBusy(true)
      setInitialTokenRecoveryError("")

      try {
        // Surface the bootstrap failure on Login after auth-state transition.
        setSessStorage(
          "auth_action_error",
          "We could not start your session. Please sign in again.",
        )

        // Avoid a hard reload here so MainApp can render Login and consume auth_action_error once.
        // Keep the timeout guard so loader does not hang forever if sign-out stalls.
        await Promise.race([
          signOut({ redirect: false }),
          new Promise((_, reject) => {
            setTimeout(() => {
              reject(new Error("Token bootstrap recovery sign-out timed out"))
            }, INITIAL_TOKEN_RECOVERY_SIGNOUT_TIMEOUT_MS)
          }),
        ])
      } catch (error) {
        LogError("Failed token bootstrap recovery sign-out", {
          errorCode: error?.code || "unknown",
          errorMessage: error?.message || "unknown",
        })

        if (!cancelled) {
          setInitialTokenRecoveryError(
            "We could not complete sign-out. Please try again.",
          )
          handledInitialTokenFailureRef.current = false
        }
      } finally {
        if (!cancelled) {
          setInitialTokenRecoveryBusy(false)
        }
      }
    }

    recoverWithSignOut()

    return () => {
      cancelled = true
    }
  }, [
    hasMainToken,
    initialTokenFetchError,
    initialTokenRecoveryRetryTick,
    signOut,
  ])

  // Keep this separate from the generic loader gate below:
  // when initial token bootstrap fails, the effect above persists a Login
  // error message and triggers signOut. We keep rendering a loader here
  // while that redirect/sign-out transition completes.
  if (initialTokenFetchError && !hasMainToken) {
    if (initialTokenRecoveryBusy) {
      return <LoaderFullPage />
    }

    if (initialTokenRecoveryError) {
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p>{initialTokenRecoveryError}</p>
          <button type="button" onClick={retryInitialTokenRecovery}>
            Retry
          </button>
        </div>
      )
    }

    return <LoaderFullPage />
  }

  // Normal startup/loading gate once bootstrap is still in progress.
  if (fetchingParticipant || !initialTokenFetchDone) {
    return <LoaderFullPage />
  }

  return (
    <UserContext.Provider value={ctxValue}>
      <UserActivityContext.Provider value={userActivityContextValue}>
        <UserActivityWarning />
        {globalLoading && <LoaderProgressBarSideWide />}
        <RoutesBuilder />
      </UserActivityContext.Provider>
    </UserContext.Provider>
  )
}

// testin cache
const ClearCacheComponent = WithClearCache(MainApp)

function App() {
  return (
    <AuthProvider>
      <Router>
        <ClearCacheComponent />
      </Router>
    </AuthProvider>
  )
}

function MainApp() {
  const { authStateReady, fetching, authUser, session, signOut } = useAuth()
  const authUid = authUser?.uid || "anonymous"
  const hasForcedAuthRouteSignOutRef = useRef(false)
  const [isForcingAuthRouteSignOut, setIsForcingAuthRouteSignOut] =
    useState(false)

  const [maintenance, setMaintenance] = useState({
    maintenance_mode: false,
    title: "",
    message: "",
    updated_at: "",
  })

  //Use this for Apollo client to track the latest token without rerendering the entire App
  const latestTokenRef = useRef(null)

  //This callback will be passed to useTokenManager to update the token whenever it is refreshed, without causing a full re-render of the App component.
  const handleMainTokenRefresh = useCallback((newToken) => {
    latestTokenRef.current = newToken
  }, [])
  const location = useLocation()

  const firebase = useContext(FirebaseContext)

  // Suscribe to Maintenance Mode Doc.
  useEffect(() => {
    const unsubscribe = firebase.db
      .collection("global-config")
      .doc("maintenance")
      .onSnapshot(
        (docSnap) => {
          if (docSnap.exists) {
            const data = docSnap.data()

            const updated_at = data.updated_at?.toDate
              ? data.updated_at.toDate().toISOString()
              : null

            setMaintenance({
              maintenance_mode: data.maintenance_mode || false,
              title: data.title || "",
              message: data.message || "",
              updated_at,
            })
          }
        },
        (error) => {
          console.error("Error listening to maintenance mode:", error)
        },
      )

    return () => unsubscribe()
  }, [firebase.db])

  // Treat missing auth/session/token as unauthenticated for app access.
  const isUnauthenticated = !authUser || !session
  const isAuthenticationRoute = location.pathname === ROUTES.AUTHENTICATION

  useEffect(() => {
    // Prevent stale auth headers while a new user's main token is being fetched.
    latestTokenRef.current = null
  }, [authUid])

  useEffect(() => {
    if (isUnauthenticated || !isAuthenticationRoute) {
      hasForcedAuthRouteSignOutRef.current = false
      setIsForcingAuthRouteSignOut(false)
      return
    }

    if (hasForcedAuthRouteSignOutRef.current) return

    hasForcedAuthRouteSignOutRef.current = true
    setIsForcingAuthRouteSignOut(true)

    let cancelled = false

    const forceSignOutForAuthRoute = async () => {
      try {
        clearSessStorage(AUTH_ROUTE_SIGNOUT_ERROR_KEY)

        // Guard against a hung sign-out call so loader does not become permanent.
        await Promise.race([
          signOut({ redirect: false }),
          new Promise((_, reject) => {
            setTimeout(() => {
              reject(new Error("Auth route forced sign-out timed out"))
            }, 10000)
          }),
        ])
      } catch (error) {
        LogError("Failed forced sign-out on authentication route", {
          errorCode: error?.code || "unknown",
          errorMessage: error?.message || "unknown",
        })

        if (!cancelled) {
          setSessStorage(
            AUTH_ROUTE_SIGNOUT_ERROR_KEY,
            "Sign-out could not be completed for this authentication action. Please sign out and sign in again.",
          )

          // Allow UI to recover and effect to retry on the next render.
          hasForcedAuthRouteSignOutRef.current = false
          setIsForcingAuthRouteSignOut(false)
        }
      }

      if (cancelled) {
        return
      }
    }

    forceSignOutForAuthRoute()

    return () => {
      cancelled = true
    }
  }, [isAuthenticationRoute, isUnauthenticated, signOut])

  useEffect(() => {
    if (!isForcingAuthRouteSignOut || !isAuthenticationRoute) return

    if (!isUnauthenticated) return

    setIsForcingAuthRouteSignOut(false)
    window.location.replace(`${ROUTES.AUTHENTICATION}${window.location.search || ""}`)
  }, [isAuthenticationRoute, isForcingAuthRouteSignOut, isUnauthenticated])

  const apolloClient = useMemo(() => {
    // Rebuild client/cache when auth identity changes to prevent cross-user
    // cache reuse after logout/login within the same mounted MainApp instance.
    const setAuthorizationLink = setContext(() => ({
      headers: { Authorization: latestTokenRef.current },
    }))
    const httpLink = new HttpLink({ uri: `${serverUrl}/graphql` })

    return new ApolloClient({
      name: `soar-client-${authUid}`,
      cache: new InMemoryCache(),
      link: setAuthorizationLink.concat(httpLink),
    })
  }, [authUid])

  if (!authStateReady || fetching) return <LoaderFullPage />

  if (isForcingAuthRouteSignOut) {
    return <LoaderFullPage />
  }

  // Maintenance mode always shows the login/maintenance screen.
  if (maintenance.maintenance_mode) {
    return <Login maintenance={maintenance} />
  }

  // If unauthenticated, use route matching so auth pages can navigate
  // within this router context (for example Back to Login from reset page).
  if (isUnauthenticated) {
    return (
      <Routes>
        <Route path={ROUTES.AUTHENTICATION} element={<Authentication />} />
        <Route path="*" element={<Login maintenance={maintenance} />} />
      </Routes>
    )
  }

  return (
    <ApolloProvider client={apolloClient}>
      <HandleIsAuth
        staff_id={authUser.uid}
        session={session}
        authUser={authUser}
        handleMainTokenRefresh={handleMainTokenRefresh}
        signOut={signOut}
      />
    </ApolloProvider>
  )
}

export default App
